import { Box, Portal } from '@chakra-ui/react';
import type { SearchBarProps } from '@time-line/components/common';
import { MotionBox } from '@time-line/components/common';
import { useCallback, useEffect } from 'react';

import { SearchResults } from '../SearchResults';
import { SearchHeader } from './SearchHeader';

interface SearchPortalProps {
  handleNavigate: () => void;
  isActive: boolean;
  headerTopPos: number;
  setIsActive: (isActive: boolean) => void;
  inputProps: SearchBarProps['inputProps'];
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
}

export function SearchPortal({
  inputProps,
  searchTerm,
  setSearchTerm,
  handleNavigate,
  isActive,
  setIsActive,
  headerTopPos,
}: SearchPortalProps) {
  const handleBack = useCallback(() => {
    setIsActive(false);
    setSearchTerm('');
  }, [setIsActive, setSearchTerm]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      handleBack();
    };
    // Capture, because a mounted Drawer stops Escape propagating on document
    // before it would ever reach a bubble-phase listener here.
    window.addEventListener('keydown', closeOnEscape, true);

    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [handleBack]);

  return (
    <Portal>
      <MotionBox
        role="dialog"
        aria-label="Site search"
        position="fixed"
        zIndex="9999"
        top={0}
        bottom={0}
        left={0}
        right={0}
        initial={{ backgroundColor: 'rgba(252,252,250,0)' }}
        animate={{
          backgroundColor: isActive
            ? 'rgba(252,252,250,1)'
            : 'rgba(252,252,250,0)',
        }}
        transition={{ duration: 0.5 }}
      >
        <MotionBox
          bg="gray.50"
          position="fixed"
          zIndex="10"
          left={0}
          px="sm"
          w="100%"
          top={`${headerTopPos}px`}
          animate={{
            top: isActive ? 0 : `${headerTopPos}px`,
          }}
          transition={{
            duration: 0.5,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          <SearchHeader inputProps={inputProps} isActive onBack={handleBack} />
        </MotionBox>
        {isActive && (
          <Box height="100%" pt="60px" px="2xs" overflowY="auto">
            <SearchResults
              handleClose={handleNavigate}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          </Box>
        )}
      </MotionBox>
    </Portal>
  );
}
